<template>
  <v-container>
    <v-card class="mb-3 elevation-2">
      <v-card-title class="primary white--text py-3">
        <v-btn icon dark @click="goBack" class="mr-2">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <v-icon dark class="mr-2">location_on</v-icon>
        <span>{{ area ? area.area_name : 'Loading...' }}</span>
        <v-spacer />
        <v-chip small dark outlined class="mr-2" v-if="area">
          <v-icon small left>access_time</v-icon>
          {{ area.timezone || 'N/A' }}
        </v-chip>
        <v-btn icon dark small @click="openMappingDialog" title="Manage Sensors" v-if="area">
          <v-icon>settings_input_component</v-icon>
        </v-btn>
        <v-btn icon dark small @click="openSensorDialog(null)" title="Add Sensor" v-if="area">
          <v-icon>add</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text v-if="area">
        <v-row dense class="mt-2">
          <v-col cols="12" sm="3">
            <div class="text-caption text--secondary">Online</div>
            <div class="text-h6">
              <v-icon small color="success">check_circle</v-icon>
              {{ onlineSensors.length }}
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="text-caption text--secondary">Offline</div>
            <div class="text-h6">
              <v-icon small color="error">error</v-icon>
              {{ offlineSensors.length }}
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="text-caption text--secondary">Total Sensors</div>
            <div class="text-h6">{{ totalSensors }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="text-caption text--secondary">Uptime</div>
            <v-progress-linear
              :value="uptimePercentage"
              :color="getUptimeColor(uptimePercentage)"
              height="20"
              rounded
              class="mt-1"
            >
              <strong>{{ uptimePercentage.toFixed(1) }}%</strong>
            </v-progress-linear>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="offlineSensors.length > 0" class="mb-3 elevation-2">
      <v-card-title class="error white--text py-3">
        <v-icon dark class="mr-2">error</v-icon>
        OFFLINE SENSORS ({{ offlineSensors.length }})
      </v-card-title>
      <v-card-text>
        <v-card
          v-for="sensor in offlineSensors"
          :key="sensor.client_id"
          class="mb-2 elevation-1"
          outlined
        >
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <div class="text-caption text--secondary">Sensor Name</div>
                <div class="text-subtitle-1 font-weight-bold">{{ sensor.client_name }}</div>
                <div class="text-caption">{{ sensor.description || 'No description' }}</div>
              </v-col>
              <v-col cols="12" md="3">
                <div class="text-caption text--secondary">Last Seen</div>
                <div class="text-body-2">{{ formatTime(sensor.last_seen_timestamp) }}</div>
                <div class="text-caption error--text font-weight-bold">
                  Offline for {{ offlineDuration(sensor.last_seen_timestamp) }}
                </div>
              </v-col>
              <v-col cols="12" md="3">
                <div class="text-caption text--secondary">Last IP Address</div>
                <div class="text-body-2">
                  <a v-if="sensor.last_public_ip" :href="`http://geoiplookup.net/ip/${sensor.last_public_ip}`" target="_blank">
                    {{ sensor.last_public_ip }}
                  </a>
                  <span v-else>N/A</span>
                </div>
              </v-col>
              <v-col cols="12" md="3" class="text-right">
                <v-btn small color="primary" @click="viewHistory(sensor)" class="mt-1 mr-1">
                  <v-icon small left>history</v-icon>
                  History
                </v-btn>
                <v-btn small icon @click="openSensorDialog(sensor)" class="mt-1 mr-1" title="Edit">
                  <v-icon small>edit</v-icon>
                </v-btn>
                <v-btn small icon color="error" @click="confirmDeleteSensor(sensor)" class="mt-1" title="Delete">
                  <v-icon small>delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <v-card v-if="onlineSensors.length > 0" class="mb-3 elevation-2">
      <v-card-title class="success white--text py-3">
        <v-icon dark class="mr-2">check_circle</v-icon>
        ONLINE SENSORS ({{ onlineSensors.length }})
      </v-card-title>
      <v-card-text>
        <v-card
          v-for="sensor in onlineSensors"
          :key="sensor.client_id"
          class="mb-2 elevation-1"
          outlined
        >
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <div class="text-caption text--secondary">Sensor Name</div>
                <div class="text-subtitle-1 font-weight-bold">{{ sensor.client_name }}</div>
                <div class="text-caption">{{ sensor.description || 'No description' }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption text--secondary">Online Duration</div>
                <div class="text-body-2 success--text font-weight-bold">
                  {{ connectionDuration(sensor.last_seen_timestamp) }}
                </div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption text--secondary">VPN IP</div>
                <div class="text-body-2">{{ sensor.last_vpn_ip || 'N/A' }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption text--secondary">Public IP</div>
                <div class="text-body-2">
                  <a v-if="sensor.last_public_ip" :href="`http://geoiplookup.net/ip/${sensor.last_public_ip}`" target="_blank">
                    {{ sensor.last_public_ip }}
                  </a>
                  <span v-else>N/A</span>
                </div>
              </v-col>
              <v-col cols="12" md="2" class="text-right">
                <v-btn small color="primary" @click="viewHistory(sensor)" class="mt-1 mr-1">
                  <v-icon small left>history</v-icon>
                  History
                </v-btn>
                <v-btn small icon @click="openSensorDialog(sensor)" class="mt-1 mr-1" title="Edit">
                  <v-icon small>edit</v-icon>
                </v-btn>
                <v-btn small icon color="error" @click="confirmDeleteSensor(sensor)" class="mt-1" title="Delete">
                  <v-icon small>delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <v-expansion-panels class="mb-3">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div>
            <v-icon class="mr-2">settings</v-icon>
            Alert Configuration
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="alertConfig.enabled"
                  label="Enable Alerts"
                  color="primary"
                  hide-details
                  class="mb-4"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="alertConfig.emailRecipient"
                  label="Email Recipient"
                  prepend-icon="email"
                  :disabled="!alertConfig.enabled"
                  hide-details
                  class="mb-4"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-caption text--secondary mb-2">
                  Offline Threshold: {{ alertConfig.offlineThreshold }} minutes
                </div>
                <v-slider
                  v-model="alertConfig.offlineThreshold"
                  :disabled="!alertConfig.enabled"
                  min="5"
                  max="120"
                  step="5"
                  thumb-label
                  prepend-icon="timer"
                  hide-details
                  class="mb-4"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-caption text--secondary mb-2">
                  Alert Cooldown: {{ alertConfig.cooldown }} minutes
                </div>
                <v-slider
                  v-model="alertConfig.cooldown"
                  :disabled="!alertConfig.enabled"
                  min="15"
                  max="240"
                  step="15"
                  thumb-label
                  prepend-icon="schedule"
                  hide-details
                  class="mb-4"
                />
              </v-col>
              <v-col cols="12" class="text-right">
                <v-btn color="primary" @click="saveAlertSettings" :disabled="!alertConfig.enabled">
                  <v-icon left>save</v-icon>
                  Save Settings
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card v-if="loading" class="text-center pa-5">
      <v-progress-circular indeterminate color="primary" />
      <div class="mt-3">Loading area details...</div>
    </v-card>

    <v-card v-if="!loading && !area" class="text-center pa-5">
      <v-icon size="64" color="grey">error_outline</v-icon>
      <div class="text-h6 mt-3">Area not found</div>
    </v-card>

    <sensor-history
      :visible="historyDialog"
      :sensor-id="historySensor ? historySensor.pi_id : ''"
      :sensor-name="historySensor ? historySensor.client_name : ''"
      :area-name="area ? area.area_name : ''"
      @close="historyDialog = false"
    />

    <sensor-form-dialog
      :visible="sensorDialogVisible"
      :sensor="selectedSensor"
      @close="sensorDialogVisible = false"
      @saved="onSensorSaved"
    />

    <sensor-mapping-dialog
      :visible="mappingDialogVisible"
      :area-id="area ? area.area_id : ''"
      :area-name="area ? area.area_name : ''"
      :sensors="sensors"
      @close="mappingDialogVisible = false"
      @updated="loadAreaData"
    />

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="error white--text">
          <v-icon dark class="mr-2">warning</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete sensor <strong>{{ sensorToDelete ? sensorToDelete.client_name : '' }}</strong>?
          <div class="mt-2 text-caption">This will also remove all history data.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteSensor">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import axios from 'axios'
import {mapState} from 'vuex'
import SensorHistory from './SensorHistory.vue'
import SensorFormDialog from './SensorFormDialog.vue'
import SensorMappingDialog from './SensorMappingDialog.vue'

export default {
  name: 'AreaDetail',
  components: {
    SensorHistory,
    SensorFormDialog,
    SensorMappingDialog
  },
  data() {
    return {
      loading: true,
      area: null,
      historyDialog: false,
      historySensor: null,
      selectedSensor: null,
      sensorDialogVisible: false,
      mappingDialogVisible: false,
      deleteDialog: false,
      sensorToDelete: null,
      deleting: false,
      alertConfig: {
        enabled: false,
        offlineThreshold: 30,
        cooldown: 60,
        emailRecipient: ''
      }
    }
  },
  async mounted() {
    await this.loadAreaData()
  },
  computed: {
    ...mapState({
      dateFormat: state => state.config.dateFormat
    }),
    sensors() {
      return this.area && this.area.sensors ? this.area.sensors : []
    },
    onlineSensors() {
      return this.sensors
        .filter(sensor => sensor.status === 'online')
        .sort((a, b) => {
          const timeA = a.last_seen_timestamp || 0
          const timeB = b.last_seen_timestamp || 0
          return timeB - timeA
        })
    },
    offlineSensors() {
      return this.sensors
        .filter(sensor => sensor.status === 'offline' || sensor.status === 'unknown')
        .sort((a, b) => {
          const timeA = a.last_seen_timestamp || 0
          const timeB = b.last_seen_timestamp || 0
          return timeA - timeB
        })
    },
    totalSensors() {
      return this.sensors.length
    },
    uptimePercentage() {
      if (this.totalSensors === 0)
        return 0
      return (this.onlineSensors.length / this.totalSensors) * 100
    }
  },
  methods: {
    async loadAreaData() {
      this.loading = true
      try {
        const {areaId} = this.$route.params
        if (!areaId) {
          console.error('No areaId in route params')
          this.area = null
          this.loading = false
          return
        }
        const response = await axios.get(`/api/areas/${areaId}`)
        this.area = response.data
        if (this.area.alert_config)
          this.alertConfig = {
            ...this.alertConfig,
            ...this.area.alert_config
          }
      } catch (error) {
        console.error('Failed to load area data:', error)
        this.area = null
      } finally {
        this.loading = false
      }
    },
    goBack() {
      this.$router.push('/sensors')
    },
    formatTime(timestamp) {
      if (!timestamp)
        return 'N/A'
      return moment(timestamp * 1000).format(this.dateFormat)
    },
    offlineDuration(lastSeenTimestamp) {
      if (!lastSeenTimestamp)
        return 'Unknown'
      return moment.duration(moment().diff(moment(lastSeenTimestamp * 1000))).humanize()
    },
    connectionDuration(connectedTimestamp) {
      if (!connectedTimestamp)
        return 'N/A'
      return moment.duration(moment().diff(moment(connectedTimestamp * 1000))).humanize()
    },
    getUptimeColor(uptime) {
      const value = parseFloat(uptime || 0)
      if (value >= 95)
        return 'success'
      if (value >= 80)
        return 'warning'
      return 'error'
    },
    viewHistory(sensor) {
      this.historySensor = sensor
      this.historyDialog = true
    },
    openSensorDialog(sensor) {
      this.selectedSensor = sensor
      this.sensorDialogVisible = true
    },
    openMappingDialog() {
      this.mappingDialogVisible = true
    },
    onSensorSaved() {
      this.loadAreaData()
    },
    confirmDeleteSensor(sensor) {
      this.sensorToDelete = sensor
      this.deleteDialog = true
    },
    async deleteSensor() {
      if (!this.sensorToDelete)
        return

      this.deleting = true
      try {
        await axios.delete(`/api/sensors/${this.sensorToDelete.pi_id}`)
        this.deleteDialog = false
        this.sensorToDelete = null
        this.loadAreaData()
      } catch (error) {
        console.error('Failed to delete sensor:', error)
        const message = (error.response && error.response.data && error.response.data.error) || 'Failed to delete sensor'
        // eslint-disable-next-line no-alert
        window.alert(message)
      } finally {
        this.deleting = false
      }
    },
    async saveAlertSettings() {
      try {
        const {areaId} = this.$route.params
        await axios.post(`/api/areas/${areaId}/alert-config`, this.alertConfig)
        console.log('Alert settings saved')
      } catch (error) {
        console.error('Failed to save alert settings:', error)
      }
    }
  },
  watch: {
    '$route.params.areaId': {
      immediate: true,
      handler(newId) {
        if (newId)
          this.loadAreaData()
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 16px !important;
}
</style>
